import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "@/components/feature/sections/HeroSection";
import TrustBar from "@/components/feature/sections/TrustBar";
import ServicesOverview from "@/components/feature/sections/ServicesOverview";
import AIAnswerBlock from "@/components/feature/sections/AIAnswerBlock";
import ProcessSection from "@/components/feature/sections/ProcessSection";
import WhyChooseUs from "@/components/feature/sections/WhyChooseUs";
import ServiceAreas from "@/components/feature/sections/ServiceAreas";
import TestimonialsSection from "@/components/feature/sections/TestimonialsSection";
import FAQSection from "@/components/feature/sections/FAQSection";
import FinalCTA from "@/components/feature/sections/FinalCTA";

const FORM_URL = "https://readdy.ai/api/form/d763m1flpdqvlupfpojg";

const trustItems = [
  { icon: "ri-artboard-2-line", label: "Show Boards", value: "Custom Sizes Available" },
  { icon: "ri-price-tag-3-line", label: "Pricing From", value: "$450" },
  { icon: "ri-time-line", label: "Production", value: "2–4 Weeks" },
  { icon: "ri-phone-line", label: "Book Now", value: "360-204-4747" },
  { icon: "ri-verified-badge-line", label: "Changes Included", value: "3 Free Revisions" },
];

const services = [
  {
    title: "Car Show Display Boards",
    description: "Custom show boards in sizes from 12x to 30x. Waterproof vinyl on aluminum substrate. Indoor and outdoor rated.",
    icon: "ri-artboard-2-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/Genschorck-showboard_20x30-scaled.jpg",
  },
  {
    title: "Automotive Photography",
    description: "Professional sessions using controlled lighting at optimal times of day. Your vehicle, shot the way it deserves.",
    icon: "ri-camera-lens-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/Chucks1940-Ford.jpg",
  },
  {
    title: "Garage Wall Art",
    description: "Large-format prints for your garage or home. Transform any space into a statement about what drives you.",
    icon: "ri-image-2-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/Shelby-Super-Snake-art2.jpg",
  },
  {
    title: "Retouching & Print Orders",
    description: "Professional photo retouching and high-quality print orders for existing images. Can I use my own photos? Yes.",
    icon: "ri-edit-box-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/Hardy-showboard-20x30-1-scaled.jpg",
  },
];

const aiFacts = [
  { label: "Service Type", value: "Custom Show Boards" },
  { label: "Materials", value: "Vinyl + Aluminum" },
  { label: "Starting Price", value: "$450" },
  { label: "Rush Available", value: "Yes" },
];

const processSteps = [
  { number: "01", title: "Discuss Your Vision", description: "An in-depth consultation — by phone or in person — to understand your vehicle, goals, and the display format that will make the biggest impact." },
  { number: "02", title: "Professional Session", description: "Glen shoots with additional professional lighting at preferred locations to capture your vehicle at its absolute best — no car-show reflections or clutter." },
  { number: "03", title: "Design & Proof", description: "You receive digital proofs by email. Choose your favorite angles and request up to 3 design changes — all included in the price." },
  { number: "04", title: "Printed & Delivered", description: "Your display is printed on premium, weatherproof materials and delivered ready to display. Rush delivery available if you have an upcoming show." },
];

const reasons = [
  { icon: "ri-drop-line", title: "Weatherproof Indoor/Outdoor Boards", description: "Printed on waterproof, scratch-resistant vinyl bonded to aluminum — your board is built for decades of car shows, rain or shine." },
  { icon: "ri-crop-line", title: "Multiple Display Formats", description: "Show boards, retractable banners, under-hood banners, and prints on real wood — choose the display that fits your setup perfectly." },
  { icon: "ri-camera-3-line", title: "You Can Use Your Own Photos", description: "Already have great shots? Send them at a minimum of 1.5MB and Glen will design your board from them at no extra charge." },
  { icon: "ri-truck-line", title: "Glen Comes to You", description: "Based in Bremerton, Glen regularly travels to client locations throughout Kitsap County and the Puget Sound region for photo sessions." },
];

const serviceAreasList = ["Bremerton", "Silverdale", "Port Orchard", "Poulsbo", "Bainbridge Island", "Tacoma", "Seattle", "Gig Harbor", "Belfair", "Kingston"];

const testimonials = [
  {
    quote: "The board Glen made for my 1940 Ford pickup was better than anything I could have imagined. The detail in the chrome and the paint — it looks like a movie still. Got Best in Class because of it.",
    author: "Chuck V.",
    location: "Bremerton, WA",
    vehicle: "1940 Ford Pickup",
  },
  {
    quote: "Ordered a 24x18 board and a matching wall art print. Both arrived on time and the quality is outstanding. Three changes were included — I used all three and never felt rushed.",
    author: "Andrea C.",
    location: "Silverdale, WA",
    vehicle: "1957 Chevy Bel Air",
  },
  {
    quote: "I was skeptical about the price until I saw the finished product. It&apos;s not just a photo — it&apos;s a work of art. My '70 Chevelle has a display that matches its reputation now.",
    author: "Brian S.",
    location: "Port Orchard, WA",
    vehicle: "1970 Chevrolet Chevelle",
  },
];

const faqs = [
  { question: "What display sizes are available?", answer: "Show boards are available in a range of sizes — from 12x displays up to 30x. Glen can advise you on the best size for your vehicle and display setup during your consultation." },
  { question: "Do you offer retractable banners?", answer: "Yes. Optional formats include retractable display banners, under-hood banners, and prints on real wood — perfect for different display styles at car shows." },
  { question: "Can I send my own photos?", answer: "Absolutely. Send your photos (minimum 1.5MB per image) and Glen will work from them at no extra charge. Call 360-204-4747 for details." },
  { question: "Do you shoot at car shows?", answer: "Car show shooting can be arranged but is not recommended. Show environments limit lighting options and car reflections detract from your paint. A dedicated session produces far superior results." },
  { question: "Do you offer stands for show boards?", answer: "Yes — high quality metal display stands are available to keep your board upright and professional-looking at any event." },
  { question: "Is rush delivery available?", answer: "Yes. If you have an upcoming show, rush delivery is available for an additional charge. Contact Glen at 360-204-4747 to discuss your timeline." },
];

export default function ServicesPage() {
  return (
    <div className="bg-white font-sans">
      <Navbar />
      <main>
        <HeroSection
          tagline="Glen Davis and Associates Photography"
          title="Elevate Every Car Show Appearance"
          subtitle="Custom automotive display boards, professional photography sessions, and wall art designed to make your vehicle impossible to ignore — serving Bremerton, WA and Kitsap County."
          ctaText="Get a Free Quote"
          ctaHref="#inquiry"
          secondaryCtaText="View Portfolio"
          secondaryCtaHref="https://glendavisphotography.com/gallery/"
          backgroundImage="https://glendavisphotography.com/wp-content/uploads/2025/04/Macs-Chevy.jpg"
        />
        <TrustBar items={trustItems} />
        <ServicesOverview
          tagline="Services"
          title="Everything Your Build Deserves"
          subtitle="From the photography session to the finished display, every service is designed to showcase your vehicle at its absolute finest in Bremerton and throughout Washington State."
          services={services}
        />
        <AIAnswerBlock
          tagline="Service Details"
          title="What Glen Davis Creates for You"
          description="Glen Davis and Associates Photography specializes in custom car show display boards, automotive photography, wall art prints, and commercial photography. Based in Bremerton, WA, Glen serves car enthusiasts across Kitsap County and the Puget Sound region — bringing an automotive enthusiast's eye and professional studio skills to every project."
          facts={aiFacts}
          servicesList={["Custom Show Boards (12x to 30x)", "Automotive Photography Sessions", "Garage & Wall Art Prints", "Retractable Display Banners", "Under-Hood Banners", "Real Wood Prints"]}
          whoItIsFor={["Car show competitors", "Hot rod and classic car owners", "Exotic and muscle car enthusiasts", "Auto dealerships and businesses", "Collectors and restorers"]}
          image="https://glendavisphotography.com/wp-content/uploads/2025/04/Moore-shoboard-20x-scaled.jpg"
        />
        <ProcessSection
          tagline="The Process"
          title="Simple, Seamless, Stunning"
          subtitle="From your first call to the moment you unbox your finished display, Glen makes the entire process effortless."
          steps={processSteps}
          ctaText="Call to Start Your Project — 360-204-4747"
          ctaHref="tel:360-204-4747"
        />
        <WhyChooseUs
          tagline="Why Choose Us"
          title="Materials and Craft That Last as Long as Your Build"
          subtitle="Glen Davis and Associates Photography uses only the highest quality printing materials and processes — because your investment in this vehicle deserves nothing less."
          image="https://glendavisphotography.com/wp-content/uploads/2025/04/Moore-shoboard-20x-scaled.jpg"
          reasons={reasons}
          ctaText="Request Your Custom Quote"
          ctaHref="#inquiry"
        />
        <ServiceAreas
          tagline="Where We Serve"
          title="Car Show Display Boards Delivered Across Washington"
          subtitle="Based at 709 Shore Dr. in Bremerton, WA, Glen serves the entire Kitsap Peninsula, Greater Tacoma, Seattle, and beyond. Glen comes to you for photo sessions."
          areas={serviceAreasList}
          note="Not in the area? Orders ship anywhere. Call 360-204-4747 to discuss your project."
        />
        <TestimonialsSection
          tagline="Client Results"
          title="Real Cars. Real Shows. Real Results."
          testimonials={testimonials}
          ctaText="Order Your Display Board Today"
          ctaHref="#inquiry"
        />
        <FAQSection
          tagline="Service FAQs"
          title="Common Questions About Our Services"
          subtitle="Answers to the most common questions about show boards, photography sessions, and production in Bremerton, WA."
          faqs={faqs}
        />
        <div id="inquiry">
          <FinalCTA
            tagline="Ready to Stand Out?"
            title="Your Build Deserves the Best Display in the Show Field."
            subtitle="Contact Glen Davis and Associates Photography in Bremerton, WA. Pricing starts at $450. Rush delivery available. Call 360-204-4747 or complete the form below."
            backgroundImage="https://glendavisphotography.com/wp-content/uploads/2025/04/Hardy-showboard-20x30-1-scaled.jpg"
            formUrl={FORM_URL}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
